/** Memorial Mode copy and helpers */

import { formatPersonDateRange } from './dateDisplay';

export const MEMORIAL_PHOTO_LIMIT = 50;

export const MEMORIAL_COPY = {
  rememberedHere: 'Remembered with love',
  memorialMode: 'Memorial Mode',
  tributePage: 'Published memorial page',
  markDeceased: 'Mark as deceased',
  remembranceLine: 'Remembrance line',
  lifeStory: 'Life story',
  audioKeepsake: 'Voice memories',
  memorialQr: 'Memorial QR',
  dynamicQr: 'Dynamic QR code',
  configureVisibility: 'Configure tribute visibility',
  leaveTribute: 'Leave a tribute',
  leaveTributeHint: 'Share a memory, message, or kind word for the family.',
  privateByDefault: 'Private by default — shared only when you choose.',
  memorialSetupHint: 'Create a private memorial space when the time is right.',
  shareWhenReady: 'Share their story only when you choose.',
  tributesModerated: 'Tributes are moderated before appearing publicly.',
  longTermPreservation: 'Long-term preservation',
  keepsakePlaque: 'Legacy Link Plaque',
  keepsakeCard: 'Memorial QR card',
};

export const DEFAULT_MEMORIAL_VISIBILITY = {
  show_stories: false,
  show_voice: false,
  show_documents: false,
  show_timeline: false,
  show_biography: true,
  show_video_links: false,
  public_tribute_enabled: false,
};

export function isMemorialActive(person) {
  const summary = person?.memorial_summary;
  if (summary) return Boolean(summary.memorial_active);
  return person?.life_state === 'remembered' && person?.memorial_mode_enabled;
}

export function isPublicTributeEnabled(person) {
  const summary = person?.memorial_summary;
  if (summary) return Boolean(summary.public_tribute_enabled);
  return Boolean(person?.memorial_settings?.visibility?.public_tribute_enabled);
}

export function getMemorialStatusLabel(memorial) {
  if (!memorial) return 'Not set up';
  if (memorial.status === 'published') return 'Published';
  if (memorial.status === 'draft') return 'Private draft';
  return 'Not set up';
}

export function getMemorialVisibilityLabel(memorial, published) {
  if (published) return 'Public (published)';
  if (memorial?.status === 'draft') return 'Private (draft)';
  if (memorial?.visibility === 'invite_only') return 'Invite only';
  return 'Private';
}

export function isMemorialPublished(memorial) {
  return memorial?.status === 'published' && memorial?.visibility === 'public';
}

export function getMemorialPath(slug) {
  return slug ? `/memorial/${slug}` : null;
}

export function getTributePath(person) {
  const slug = person?.memorial_summary?.tribute_slug || person?.slug;
  return slug ? `/remember/${slug}` : null;
}

export function getMemorialPublicUrl(publicUrl, slug) {
  if (publicUrl) return publicUrl;
  if (typeof window !== 'undefined' && slug) {
    return `${window.location.origin}/memorial/${slug}`;
  }
  return null;
}

export function mergeVisibility(vis) {
  return { ...DEFAULT_MEMORIAL_VISIBILITY, ...(vis || {}) };
}

/** Logged-in memorial workspace route (not a person tab). */
export function memorialManageHref(person) {
  if (!person?.id) return '/memorials';
  if (person.slug) return `/memorials/manage/${person.slug}`;
  return `/memorials/manage/${person.id}`;
}

export function memorialsHref(treeId) {
  if (treeId) return `/memorials?tree=${treeId}`;
  return '/memorials';
}

export const MEMORIAL_SUPPORT_TYPES = {
  family: 'Support the family',
  funeral: 'Help with funeral costs',
  charity: 'Donate to chosen charity',
  memorial_stone: 'Contribute to a memorial stone',
  other: 'External support page',
};

export const DEFAULT_PUBLIC_SECTIONS = {
  show_life_story: false,
  show_gallery: false,
  show_tributes: false,
  show_reactions: false,
  show_voice: false,
  show_soundtrack: false,
  show_videos: false,
  show_qr: false,
  show_keepsakes: false,
  show_support: false,
  show_atmosphere: false,
};

export const MEMORIAL_PUBLIC_TABS = [
  { id: 'life-story', label: 'Life Story', sectionKey: 'show_life_story' },
  { id: 'gallery', label: 'Gallery', sectionKey: 'show_gallery' },
  { id: 'remembrance', label: 'Quiet remembrance', sectionKey: 'show_reactions' },
  { id: 'voice-music', label: 'Memory Soundtrack', sectionKey: 'show_voice' },
  { id: 'videos', label: 'Videos', sectionKey: 'show_videos' },
  { id: 'tributes', label: 'Tributes', sectionKey: 'show_tributes' },
  { id: 'qr-keepsakes', label: 'QR & Keepsakes', sectionKey: 'show_qr' },
  { id: 'support', label: 'Support', sectionKey: 'show_support' },
  { id: 'atmosphere', label: 'Atmosphere', sectionKey: 'show_atmosphere' },
];

export function mergePublicSections(sections) {
  return { ...DEFAULT_PUBLIC_SECTIONS, ...(sections || {}) };
}

/** Dedicated cover only — never reuse portrait/profile as a stretched banner. */
export function resolveMemorialCoverUrl(memorial, person, backendBase, resolveAssetUrlFn) {
  const coverRaw = (memorial?.cover_photo_url || '').trim();
  if (!coverRaw) return null;
  const portraitRaw = (memorial?.portrait_photo_url || person?.profile_image_url || '').trim();
  const coverResolved = resolveAssetUrlFn(backendBase, coverRaw);
  if (!portraitRaw) return coverResolved;
  if (coverRaw === portraitRaw) return null;
  const portraitResolved = resolveAssetUrlFn(backendBase, portraitRaw);
  if (coverResolved === portraitResolved) return null;
  return coverResolved;
}

export function resolveMemorialPortraitUrl(memorial, person, backendBase, resolveAssetUrlFn) {
  const raw = (memorial?.portrait_photo_url || person?.profile_image_url || '').trim();
  return raw ? resolveAssetUrlFn(backendBase, raw) : null;
}

export function personDisplayName(person) {
  if (!person) return 'Unknown';
  return [person.given_names, person.surname].filter(Boolean).join(' ') || 'Unknown';
}

/** User-facing birth/death line for memorial lists and cards. */
export function formatPersonDates(person) {
  if (!person) return null;
  const formatted = formatPersonDateRange(person);
  return formatted || null;
}
